import { api } from "@/api/client";
import type { ListingSummaryDto } from "@/api/buyers";

/**
 * The Present-a-buyer picker's **Suggested** tier — how well each listing in the
 * catalog fits the buyer being presented.
 *
 * <p><b>Why it lives here rather than in `api/buyers.ts`.</b> Every other listing
 * call is CRUD over a shared catalog that knows nothing about who is asking;
 * this one is a ranking against one buyer's criteria and exists to serve exactly
 * one surface. Kept beside that surface until a second consumer earns it a place
 * in the module's shared client.</p>
 *
 * <p>The whole response is composed server-side, <b>including the reason
 * sentences</b>. That is deliberate: reasons derived on the client could word
 * the same fact differently from the score that ordered the row, and a ranking
 * whose explanation disagrees with it is worse than one with no explanation.</p>
 */

/** One dimension's verdict. `unknown` is NOT a miss — it says one SIDE is
 *  blank, which is a prompt to fill something in rather than evidence against
 *  the listing. Widened to `string` so a new server verdict renders as itself
 *  instead of breaking the build on a display concern. */
export type ListingMatchVerdict = "hit" | "near" | "miss" | "unknown" | (string & {});

export interface ListingMatchReasonDto {
  /** Display noun — "Budget", "Cash flow", "Industry", … */
  dimension: string;
  verdict: ListingMatchVerdict;
  /** Points out of 100 this dimension is worth, so the ordering is arguable. */
  weight: number;
  /** The whole sentence, composed server-side. */
  detail: string;
}

export interface ListingMatchDto {
  listing: ListingSummaryDto;
  /** 0–100, or null when NOT ONE dimension could be measured. Null is not zero:
   *  a listing nothing could be measured against has not been judged badly, it
   *  has not been judged. */
  score: number | null;
  scoredDimensions: number;
  totalDimensions: number;
  reasons: ListingMatchReasonDto[];
}

/** A buyer criterion no listing column can answer. Rendered with NO verdict. */
export interface ListingMatchCriterionDto {
  criterion: string;
  why: string;
}

export interface ListingMatchesDto {
  matches: ListingMatchDto[];
  /** Listings with no asking price — their own group, still scored on every
   *  other dimension. Never silently dropped. */
  noPriceStated: ListingMatchDto[];
  /** The true count, so a COLLAPSED group can still say how many are inside. */
  noPriceStatedTotal: number;
  candidatesConsidered: number;
  /** The ranking is over the newest N rather than the whole catalog. */
  candidatesTruncated: boolean;
  notConsidered: ListingMatchCriterionDto[];
  /** False when the buyer records nothing to match on — the tier then prompts
   *  for criteria rather than showing "newest first" wearing a score. */
  buyerHasCriteria: boolean;
  /** Dropped because this buyer already has a submission against them. */
  alreadyPresentedExcluded: number;
}

export const listingMatchApi = {
  forBuyer: (buyerLeadId: string) =>
    api.get<ListingMatchesDto>("/listings/matches", { buyerLeadId }),
};
